import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Tabs,
  Tooltip,
  Button,
  Spin,
  Empty,
  Badge,
  Modal,
  message,
  Select,
  Switch,
  Tag,
  Table,
  Radio,
  DatePicker,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { MdPayment, MdAccountBalance, MdOutbox, MdBolt } from "react-icons/md";
import {
  RiRefreshLine,
  RiTimeLine,
  RiCheckLine,
  RiCloseLine,
  RiLoader4Line,
  RiPauseLine,
  RiAlertLine,
  RiInformationLine,
  RiStopCircleLine,
} from "react-icons/ri";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "layout/DashboardLayout";
import {
  CreditorOutgoingJobData,
  JobName,
  PaymentAttemptDto,
  PaymentAttemptStatus,
  PaymentComponent,
  PaymentJobCategory,
  PaymentJobDto,
  PaymentJobStatus,
} from "dtos/payment.job.dto";
import { PaymentMethods } from "dtos/application.dto";
import { Gateway } from "dtos/wallet.transaction.dto";
import { ApiService } from "api/api.service";
import { queryClient } from "utils/queryClient";
import PageTitle from "components/PageTitle";
import { TbLink } from "react-icons/tb";
import { WalletTypes } from "dtos/wallet.dto";
import {
  formatAmount,
  formatDate,
  getAttemptCount,
  getCreditorName,
  getCreditorWalletCount,
  getIsPartialCreditor,
  getJobAmount,
  getJobComponent,
  getJobPmIndex,
  getJobSlot,
  getJobTitleLabel,
  getSlotTiming,
  readLs,
  writeLs,
} from "./helpers";
import { CHARGE_ATTEMPTS_TAB, LS_REFRESH_ENABLED, LS_REFRESH_INTERVAL, REFRESH_OPTIONS, TabKey } from "./constants";
import dayjs, { Dayjs } from "dayjs";

const STATUS_CONFIG: Record<PaymentJobStatus, { color: string; bg: string; icon: React.ReactNode; label: string }> = {
  [PaymentJobStatus.SCHEDULED]: { color: "#64748b", bg: "#f1f5f9", icon: <RiTimeLine />, label: "Scheduled" },
  [PaymentJobStatus.RUNNING]: {
    color: "#2563eb",
    bg: "#eff6ff",
    icon: <RiLoader4Line className="animate-spin" />,
    label: "Running",
  },
  [PaymentJobStatus.COMPLETED]: { color: "#16a34a", bg: "#f0fdf4", icon: <RiCheckLine />, label: "Completed" },
  [PaymentJobStatus.FAILED]: { color: "#dc2626", bg: "#fef2f2", icon: <RiCloseLine />, label: "Failed" },
  [PaymentJobStatus.LAPSED]: { color: "#d97706", bg: "#fffbeb", icon: <RiAlertLine />, label: "Lapsed" },
  [PaymentJobStatus.CANCELLED]: { color: "#9333ea", bg: "#faf5ff", icon: <RiStopCircleLine />, label: "Cancelled" },
  [PaymentJobStatus.SUPERSEDED]: { color: "#0ea5e9", bg: "#f0f9ff", icon: <RiRefreshLine />, label: "Superseded" },
};

const ATTEMPT_STATUS_CONFIG: Record<
  PaymentAttemptStatus,
  { color: string; bg: string; icon: React.ReactNode; label: string }
> = {
  [PaymentAttemptStatus.PENDING]: { color: "#64748b", bg: "#f1f5f9", icon: <RiTimeLine />, label: "Pending" },
  [PaymentAttemptStatus.AWAITING_APPROVAL]: {
    color: "#d97706",
    bg: "#fffbeb",
    icon: <RiLoader4Line className="animate-spin" />,
    label: "Awaiting Approval",
  },
  [PaymentAttemptStatus.SUCCEEDED]: { color: "#16a34a", bg: "#f0fdf4", icon: <RiCheckLine />, label: "Succeeded" },
  [PaymentAttemptStatus.FAILED]: { color: "#dc2626", bg: "#fef2f2", icon: <RiCloseLine />, label: "Failed" },
};

const COMPONENT_LABELS: Record<PaymentComponent, { label: string; tooltip: string }> = {
  [PaymentComponent.ABCD]: { label: "ABCD", tooltip: "Program Fee + Legal Fee + Service Fee + Creditor Total" },
  [PaymentComponent.ABC]: { label: "ABC", tooltip: "Program Fee + Legal Fee + Service Fee" },
  [PaymentComponent.BC]: { label: "BC", tooltip: "Service Fee + Legal Fee" },
  [PaymentComponent.D_ONLY]: { label: "D Only", tooltip: "Only Creditor Total" },
};

const GATEWAY_LABELS: Record<Gateway, string> = {
  [Gateway.STRIPE]: "Stripe",
  [Gateway.SEAMLESSCHEX]: "SeamlessChex",
  [Gateway.SEAMLESSCHEX_MERCHANT]: "SeamlessChex (Merchant)",
  [Gateway.PAYNOTE]: "Paynote",
  [Gateway.MANUALLY_MARKED]: "Manually Marked",
  [Gateway.AUTOMATED_DISTRIBUTION]: "Automated Distribution",
};

const METHOD_LABELS: Record<PaymentMethods, string> = {
  [PaymentMethods.CREDIT_CARD]: "Credit Card",
  [PaymentMethods.ECHECK]: "eCheck",
  [PaymentMethods.PAYNOTE]: "Paynote",
  [PaymentMethods.PAYNOTE_PLAID]: "Paynote × Plaid",
};

const CATEGORY_META: Record<PaymentJobCategory, { label: string; icon: React.ReactNode; description: string }> = {
  [PaymentJobCategory.PAYMENT]: {
    label: "Payment",
    icon: <MdPayment />,
    description:
      "Jobs that attempt to collect funds from the client — includes waterfall scheduling, time-based slots, and component-level retries.",
  },
  [PaymentJobCategory.DISTRIBUTION]: {
    label: "Distribution",
    icon: <MdAccountBalance />,
    description: "Jobs that distribute collected funds into internal wallets after a successful payment.",
  },
  [PaymentJobCategory.CREDITOR_OUTGOING]: {
    label: "Creditor Outgoing",
    icon: <MdOutbox />,
    description:
      "Jobs that send money out to creditors. Can run as a full creditor batch or as per-creditor partial amounts when the full amount fails.",
  },
};

const ATTEMPT_COLUMNS: ColumnsType<PaymentAttemptDto> = [
  {
    title: "Week",
    dataIndex: "week",
    key: "week",
    width: 72,
    sorter: (a, b) => a.week - b.week,
    render: (w: number) => <span className="font-mono text-slate-600 text-xs">W{w}</span>,
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 160,
    filters: Object.values(PaymentAttemptStatus).map((s) => ({
      text: ATTEMPT_STATUS_CONFIG[s].label,
      value: s,
    })),
    onFilter: (value, record) => record.status === value,
    render: (s: PaymentAttemptStatus) => <AttemptStatusBadge status={s} />,
  },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    width: 110,
    sorter: (a, b) => a.amount - b.amount,
    render: (v: number) => <span className="font-semibold text-slate-800">{formatAmount(v)}</span>,
  },
  {
    title: "Component",
    dataIndex: "component",
    key: "component",
    width: 120,
    filters: Object.values(PaymentComponent).map((c) => ({
      text: COMPONENT_LABELS[c].label,
      value: c,
    })),
    onFilter: (value, record) => record.component === value,
    render: (c: PaymentComponent) => (
      <Tooltip title={COMPONENT_LABELS[c]?.tooltip}>
        <Tag className="cursor-help font-mono text-xs">{COMPONENT_LABELS[c]?.label ?? c}</Tag>
      </Tooltip>
    ),
  },
  {
    title: "Gateway",
    dataIndex: "gateway",
    key: "gateway",
    width: 170,
    filters: Object.values(Gateway).map((g) => ({ text: GATEWAY_LABELS[g], value: g })),
    onFilter: (value, record) => record.gateway === value,
    render: (g: Gateway) => <span className="text-xs text-slate-600">{GATEWAY_LABELS[g] ?? g}</span>,
  },
  {
    title: "Method",
    dataIndex: "payment_method_type",
    key: "payment_method_type",
    width: 140,
    filters: Object.values(PaymentMethods).map((m) => ({ text: METHOD_LABELS[m], value: m })),
    onFilter: (value, record) => record.payment_method_type === value,
    render: (m: PaymentMethods) => <span className="text-xs text-slate-600">{METHOD_LABELS[m] ?? m}</span>,
  },
  {
    title: "Attempted",
    dataIndex: "attempted_at",
    key: "attempted_at",
    width: 160,
    sorter: (a, b) => new Date(a.attempted_at ?? "").getTime() - new Date(b.attempted_at ?? "").getTime(),
    render: (v?: string) => <span className="text-xs text-slate-500">{formatDate(v)}</span>,
  },
  {
    title: "Resolved",
    dataIndex: "resolved_at",
    key: "resolved_at",
    width: 160,
    sorter: (a, b) => new Date(a.resolved_at ?? "").getTime() - new Date(b.resolved_at ?? "").getTime(),
    render: (v?: string) => <span className="text-xs text-slate-500">{formatDate(v)}</span>,
  },
  {
    title: "Failure Reason",
    dataIndex: "failure_reason",
    key: "failure_reason",
    ellipsis: true,
    render: (v?: string) =>
      v ? (
        <Tooltip title={v}>
          <span className="text-xs text-red-600 cursor-help">{v}</span>
        </Tooltip>
      ) : (
        <span className="text-slate-300 text-xs">—</span>
      ),
  },
];

const CountdownDisplay = React.memo(function CountdownDisplay({ seconds }: { seconds: number }) {
  return (
    <Tooltip title="Refreshes in">
      <span className="font-mono font-semibold tabular-nums min-w-[24px] text-center text-blue-600">{seconds}s</span>
    </Tooltip>
  );
});

function StatusBadge({ status }: { status: PaymentJobStatus }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.color}22` }}
    >
      <span className="text-[11px]">{cfg.icon}</span>
      {cfg.label}
    </span>
  );
}

function AttemptStatusBadge({ status }: { status: PaymentAttemptStatus }) {
  const cfg = ATTEMPT_STATUS_CONFIG[status];
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.color}22` }}
    >
      <span className="text-[11px]">{cfg.icon}</span>
      {cfg.label}
    </span>
  );
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="text-slate-400 block mb-0.5">{label}</span>
      <span className="font-medium text-slate-700 text-xs">{children}</span>
    </div>
  );
}

function JobCard({
  job,
  onLapse,
  onResumeDistribution,
  onResumeCreditor,
  onReschedule,
  lapsing,
}: {
  job: PaymentJobDto;
  onLapse: (id: string) => void;
  onResumeDistribution: (id: string) => void;
  onResumeCreditor: (id: string) => void;
  onReschedule: (job: PaymentJobDto) => void;
  lapsing?: boolean;
}) {
  const isDistribution = job.category === PaymentJobCategory.DISTRIBUTION;
  const isCreditorOutgoing = job.category === PaymentJobCategory.CREDITOR_OUTGOING;
  const isCreditorOutgoingManuallyResolved =
    job.category === PaymentJobCategory.CREDITOR_OUTGOING &&
    (job.job_data as CreditorOutgoingJobData).manually_resolved;

  const canLapse = isDistribution && job.status === PaymentJobStatus.SCHEDULED;

  const canResumeDistribution = isDistribution && job.status === PaymentJobStatus.LAPSED;

  const canLapseCreditor = isCreditorOutgoing && job.status === PaymentJobStatus.SCHEDULED;

  const canResumeCreditor = isCreditorOutgoing && job.status === PaymentJobStatus.LAPSED;

  const canReschedule = job.job_name === JobName.PAYMENT_SLOT && job.status === PaymentJobStatus.SCHEDULED;

  const amount = getJobAmount(job);
  const component = getJobComponent(job);
  const slot = getJobSlot(job);
  const creditorName = getCreditorName(job);
  const creditorCount = getCreditorWalletCount(job);
  const attemptCount = getAttemptCount(job);
  const isPartial = getIsPartialCreditor(job);

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
      <div
        className="h-1 w-full"
        style={{
          background:
            job.status === PaymentJobStatus.RUNNING
              ? "linear-gradient(90deg, #2563eb 0%, #7c3aed 50%, #2563eb 100%)"
              : STATUS_CONFIG[job.status].color,
          backgroundSize: job.status === PaymentJobStatus.RUNNING ? "200% 100%" : undefined,
          animation: job.status === PaymentJobStatus.RUNNING ? "shimmer 1.8s linear infinite" : undefined,
        }}
      />

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-semibold text-slate-800 leading-tight">{getJobTitleLabel(job.job_name)}</span>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-400 font-mono">{job._id}</span>
              <span className="text-[10px] text-slate-300">·</span>
              <span className="text-[10px] text-slate-400">Week {job.week}</span>
            </div>
          </div>
          <StatusBadge status={job.status} />
        </div>

        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5 text-xs">
          {amount != null && (
            <InfoRow label="Amount">
              <span className="font-semibold text-slate-800 text-sm">{formatAmount(amount)}</span>
            </InfoRow>
          )}
          {component && (
            <InfoRow label="Component">
              <Tooltip title={COMPONENT_LABELS[component].tooltip}>
                <span className="inline-flex items-center gap-1 cursor-help">
                  {COMPONENT_LABELS[component].label}
                  <RiInformationLine className="text-slate-300" />
                </span>
              </Tooltip>
            </InfoRow>
          )}
          {slot != null && <InfoRow label="Slot">{getSlotTiming(slot)}</InfoRow>}
          {creditorName && <InfoRow label="Creditor">{creditorName}</InfoRow>}
          {creditorCount != null && <InfoRow label="Creditors">{creditorCount}</InfoRow>}
          {attemptCount != null && <InfoRow label="Attempts">{attemptCount}</InfoRow>}
          {isPartial != null && (
            <InfoRow label="Partial">
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold"
                style={
                  isPartial ? { background: "#fef9c3", color: "#854d0e" } : { background: "#f0fdf4", color: "#166534" }
                }
              >
                {isPartial ? "Yes" : "No"}
              </span>
            </InfoRow>
          )}
          {isCreditorOutgoingManuallyResolved && (
            <InfoRow label="Resolution">
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-50 text-purple-700">
                Manually Resolved
              </span>
            </InfoRow>
          )}
          <InfoRow label="Scheduled">{formatDate(job.scheduled_at)}</InfoRow>
          {job.started_at && <InfoRow label="Started">{formatDate(job.started_at)}</InfoRow>}
          {job.completed_at && <InfoRow label="Completed">{formatDate(job.completed_at)}</InfoRow>}
          {job.failed_at && <InfoRow label="Failed at">{formatDate(job.failed_at)}</InfoRow>}
        </div>

        {job.failure_reason && (
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-xs text-red-700">
            <RiAlertLine className="mt-0.5 shrink-0 text-red-400" />
            <span>{job.failure_reason}</span>
          </div>
        )}

        {canReschedule && (
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
            <Tooltip title="Move this job's run time earlier than currently scheduled.">
              <span className="text-xs text-slate-400 flex items-center gap-1 cursor-help">
                <RiInformationLine /> Reschedule available
              </span>
            </Tooltip>
            <Button size="small" icon={<RiTimeLine />} onClick={() => onReschedule(job)}>
              Run Now
            </Button>
          </div>
        )}

        {canLapse && (
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
            <Tooltip title="Manually mark this distribution job as lapsed.">
              <span className="text-xs text-slate-400 flex items-center gap-1 cursor-help">
                <RiInformationLine /> Lapse available
              </span>
            </Tooltip>
            <Button size="small" danger icon={<RiPauseLine />} loading={lapsing} onClick={() => onLapse(job._id!)}>
              Lapse
            </Button>
          </div>
        )}

        {canResumeDistribution && (
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
            <Tooltip title="Un-lapse this job so it can be retried later.">
              <span className="text-xs text-slate-400 flex items-center gap-1 cursor-help">
                <RiInformationLine /> Currently lapsed
              </span>
            </Tooltip>
            <Button
              size="small"
              type="primary"
              icon={<RiRefreshLine />}
              loading={lapsing}
              onClick={() => onResumeDistribution(job._id!)}
            >
              Resume
            </Button>
          </div>
        )}

        {canLapseCreditor && (
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
            <Tooltip title="Stop this creditor outgoing job. Once lapsed, you can resume it via real payout or manually reconciling in the wallet.">
              <span className="text-xs text-slate-400 flex items-center gap-1 cursor-help">
                <RiInformationLine /> Lapse available
              </span>
            </Tooltip>
            <Button size="small" danger icon={<RiPauseLine />} loading={lapsing} onClick={() => onLapse(job._id!)}>
              Lapse
            </Button>
          </div>
        )}

        {canResumeCreditor && (
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
            <Tooltip title="Retry this creditor outgoing job. Choose to send a real payout or manually reconcile wallets only (if already paid externally).">
              <span className="text-xs text-slate-400 flex items-center gap-1 cursor-help">
                <RiInformationLine /> Resume available
              </span>
            </Tooltip>
            <Button
              size="small"
              type="primary"
              icon={<RiRefreshLine />}
              loading={lapsing}
              onClick={() => onResumeCreditor(job._id!)}
            >
              Resume
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryPanel({
  category,
  jobs,
  onLapse,
  onResumeDistribution,
  onResumeCreditor,
  onReschedule,
  lapsingId,
}: {
  category: PaymentJobCategory;
  jobs: PaymentJobDto[];
  onLapse: (id: string) => void;
  onResumeDistribution: (id: string) => void;
  onResumeCreditor: (id: string) => void;
  onReschedule: (job: PaymentJobDto) => void;
  lapsingId: string | null;
}) {
  const meta = CATEGORY_META[category];
  const statusCounts = jobs.reduce(
    (acc, j) => ({ ...acc, [j.status]: (acc[j.status] ?? 0) + 1 }),
    {} as Record<string, number>,
  );

  return (
    <div>
      <div className="mb-5 flex items-start gap-3 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
        <span className="text-xl text-slate-400 mt-0.5">{meta.icon}</span>
        <p className="text-sm text-slate-500 leading-relaxed">{meta.description}</p>
      </div>

      {jobs.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          {Object.entries(statusCounts).map(([status, count]) => {
            const cfg = STATUS_CONFIG[status as PaymentJobStatus];
            return (
              <span
                key={status}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
                style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}33` }}
              >
                {cfg.icon}
                {cfg.label}
                <span
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold"
                  style={{ background: cfg.color, color: "#fff" }}
                >
                  {count}
                </span>
              </span>
            );
          })}
        </div>
      )}

      {jobs.length === 0 ? (
        <Empty description="No jobs in this category" className="py-12" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {jobs.map((job) => (
            <JobCard
              key={job._id}
              job={job}
              onLapse={onLapse}
              onResumeDistribution={onResumeDistribution}
              onResumeCreditor={onResumeCreditor}
              onReschedule={onReschedule}
              lapsing={lapsingId === job._id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ChargeAttemptsPanel({ attempts, isLoading }: { attempts: PaymentAttemptDto[]; isLoading: boolean }) {
  const statusCounts = attempts.reduce(
    (acc, a) => ({ ...acc, [a.status]: (acc[a.status] ?? 0) + 1 }),
    {} as Record<string, number>,
  );

  const totalSucceeded = attempts
    .filter((a) => a.status === PaymentAttemptStatus.SUCCEEDED)
    .reduce((sum, a) => sum + a.amount, 0);

  return (
    <div>
      <div className="mb-5 flex items-start gap-3 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
        <span className="text-xl text-slate-400 mt-0.5">
          <MdBolt />
        </span>
        <p className="text-sm text-slate-500 leading-relaxed">
          Individual payment charge attempts made against the client&apos;s payment method — each represents a single
          gateway transaction. A single payment job may produce multiple attempts across different gateways or methods.
        </p>
      </div>

      {attempts.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-5">
          {Object.entries(statusCounts).map(([status, count]) => {
            const cfg = ATTEMPT_STATUS_CONFIG[status as PaymentAttemptStatus];
            return (
              <span
                key={status}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
                style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}33` }}
              >
                {cfg.icon}
                {cfg.label}
                <span
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold"
                  style={{ background: cfg.color, color: "#fff" }}
                >
                  {count}
                </span>
              </span>
            );
          })}
          {totalSucceeded > 0 && (
            <span className="ml-auto text-xs font-semibold text-slate-700 bg-green-50 border border-green-200 px-3 py-1 rounded-full">
              Total collected: {formatAmount(totalSucceeded)}
            </span>
          )}
        </div>
      )}

      {attempts.length === 0 && !isLoading ? (
        <Empty description="No charge attempts found" className="py-12" />
      ) : (
        <Table<PaymentAttemptDto>
          dataSource={attempts}
          columns={ATTEMPT_COLUMNS}
          rowKey="_id"
          loading={isLoading}
          size="small"
          scroll={{ x: 1100 }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            showTotal: (total) => `${total} attempt${total !== 1 ? "s" : ""}`,
            size: "small",
          }}
          rowClassName={(record) =>
            record.status === PaymentAttemptStatus.FAILED
              ? "bg-red-50/40"
              : record.status === PaymentAttemptStatus.SUCCEEDED
                ? "bg-green-50/30"
                : ""
          }
          expandable={{
            expandedRowRender: (record) =>
              record.external_transaction_id ? (
                <div className="py-1 px-2 text-xs text-slate-500">
                  External Transaction ID:{" "}
                  <span className="font-mono text-slate-700">{record.external_transaction_id}</span>
                </div>
              ) : null,
            rowExpandable: (record) => !!record.external_transaction_id,
          }}
        />
      )}
    </div>
  );
}

export default function PaymentJobMonitor() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const ledger_id = searchParams.get("ledger_id") ?? undefined;
  const application_id = searchParams.get("application_id") ?? undefined;
  const appkey = searchParams.get("appkey") ?? undefined;

  const [activeTab, setActiveTab] = useState<TabKey>(PaymentJobCategory.PAYMENT);
  const [lapsingId, setLapsingId] = useState<string | null>(null);

  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState<boolean>(() => readLs(LS_REFRESH_ENABLED, false));
  const [refreshInterval, setRefreshInterval] = useState<number>(() => readLs(LS_REFRESH_INTERVAL, 10));
  const [countdown, setCountdown] = useState<number>(refreshInterval);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // --- reschedule modal state ---
  const [reschedulingJob, setReschedulingJob] = useState<PaymentJobDto | null>(null);
  const [rescheduleMode, setRescheduleMode] = useState<"now" | "custom">("now");
  const [customTime, setCustomTime] = useState<Dayjs | null>(null);

  const hasParams = !!ledger_id && !!application_id;

  const {
    data: apiData,
    isLoading,
    isFetching,
    refetch,
  } = ApiService.paymentJob.useGetJobsByLedger({
    ledger_id,
    application_id,
  });
  const {
    data: attemptsData,
    isLoading: attemptsLoading,
    refetch: refetchAttempts,
  } = ApiService.paymentJob.useGetPaymentChargeAttempts({ ledger_id, application_id });

  const rescheduleJob = ApiService.paymentJob.useReschedulePaymentJob();

  const jobs: PaymentJobDto[] = apiData?.result ?? [];
  const attempts: PaymentAttemptDto[] = attemptsData?.result ?? [];

  useEffect(() => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (!autoRefreshEnabled || !hasParams) {
      setCountdown(refreshInterval);
      return;
    }
    setCountdown(refreshInterval);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          refetch();
          refetchAttempts();
          return refreshInterval;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [autoRefreshEnabled, refreshInterval, hasParams, refetchAttempts]);

  const handleToggleAutoRefresh = (val: boolean) => {
    setAutoRefreshEnabled(val);
    writeLs(LS_REFRESH_ENABLED, val);
    setCountdown(refreshInterval);
  };

  const handleIntervalChange = (val: number) => {
    setRefreshInterval(val);
    writeLs(LS_REFRESH_INTERVAL, val);
    setCountdown(val);
  };

  const { mutate: toggleLapse } = ApiService.paymentJob.useToggleDistributionJobLapse();
  const { mutate: resumeCreditor } = ApiService.paymentJob.useResumeCreditorOutgoingJob();

  const invalidateJobs = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["payment-jobs", ledger_id, application_id] });
  }, [ledger_id, application_id]);

  const handleLapse = useCallback(
    (job_id: string) => {
      Modal.confirm({
        title: "Lapse this job?",
        content: "This will manually mark the job as lapsed and stop it from running automatically.",
        okText: "Yes, lapse it",
        okButtonProps: { danger: true },
        cancelText: "Cancel",
        onOk: () => {
          setLapsingId(job_id);
          toggleLapse(
            { job_id },
            {
              onSuccess: () => {
                message.success("Job lapsed successfully.");
                invalidateJobs();
              },
              onSettled: () => setLapsingId(null),
            },
          );
        },
      });
    },
    [toggleLapse, invalidateJobs],
  );

  const handleResumeDistribution = useCallback(
    (job_id: string) => {
      Modal.confirm({
        title: "Resume this distribution job?",
        content: "This will un-lapse the job and allow it to be retried automatically.",
        okText: "Yes, resume it",
        cancelText: "Cancel",
        onOk: () => {
          setLapsingId(job_id);
          toggleLapse(
            { job_id },
            {
              onSuccess: () => {
                message.success("Job resumed successfully.");
                invalidateJobs();
              },
              onSettled: () => setLapsingId(null),
            },
          );
        },
      });
    },
    [toggleLapse, invalidateJobs],
  );

  const handleResumeCreditor = useCallback(
    (job_id: string) => {
      const fireResume = (mode: "actual" | "virtual") => {
        Modal.destroyAll();
        setLapsingId(job_id);
        resumeCreditor(
          { payment_job_id: job_id, mode },
          {
            onSuccess: () => {
              message.success(`Creditor job has been resumed.`);
              invalidateJobs();
            },
            onSettled: () => setLapsingId(null),
          },
        );
      };

      Modal.confirm({
        title: "Resume creditor outgoing job?",
        icon: null,
        width: 500,
        content: (
          <div className="text-sm text-slate-600 pt-1 space-y-3">
            <p className="text-slate-500">Choose how to resume this creditor outgoing job:</p>

            <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
              <p className="font-semibold text-blue-800 mb-1">Attempt Outgoing Payment</p>
              <p className="text-blue-700 text-xs leading-relaxed">
                Schedules a new creditor outgoing job to attempt a real payout to the creditor.
              </p>
            </div>

            <div className="rounded-lg border border-violet-100 bg-violet-50 px-4 py-3">
              <p className="font-semibold text-violet-800 mb-1">Manually Mark Payment</p>
              <p className="text-violet-700 text-xs leading-relaxed">
                Performs manual wallet distribution directly to the creditor wallet. Use this when the creditor has
                already been paid externally and you need to reconcile the internal wallets.
              </p>
            </div>
          </div>
        ),
        footer: () => (
          <div className="flex justify-end gap-2 pt-3">
            <Button onClick={() => Modal.destroyAll()}>Cancel</Button>
            <Button onClick={() => fireResume("virtual")}>Manually Reconcile</Button>
            <Button type="primary" onClick={() => fireResume("actual")}>
              Schedule Real Payout
            </Button>
          </div>
        ),
      });
    },
    [resumeCreditor, invalidateJobs],
  );

  // --- reschedule handlers ---
  const handleOpenReschedule = useCallback((job: PaymentJobDto) => {
    setReschedulingJob(job);
    setRescheduleMode("now");
    setCustomTime(null);
  }, []);

  const handleCloseReschedule = useCallback(() => {
    setReschedulingJob(null);
    setRescheduleMode("now");
    setCustomTime(null);
  }, []);

  const handleConfirmReschedule = useCallback(() => {
    if (!reschedulingJob) return;

    if (rescheduleMode === "custom" && !customTime) {
      message.error("Pick a custom time first.");
      return;
    }

    rescheduleJob.mutate(
      {
        paymentJobId: reschedulingJob._id!,
        customTime: rescheduleMode === "custom" ? customTime!.toISOString() : undefined,
      },
      {
        onSuccess: () => {
          message.success("Payment job rescheduled successfully.");
          invalidateJobs();
          handleCloseReschedule();
        },
        onError: (err: any) => {
          message.error(err?.response?.data?.message ?? "Failed to reschedule job.");
        },
      },
    );
  }, [reschedulingJob, rescheduleMode, customTime, rescheduleJob, invalidateJobs, handleCloseReschedule]);

  const rescheduleCeiling = reschedulingJob ? dayjs(reschedulingJob.scheduled_at) : null;

  const tabItems = useMemo(() => {
    const jobTabs = Object.values(PaymentJobCategory).map((cat) => {
      const catJobs = jobs.filter((j) => j.category === cat);
      const meta = CATEGORY_META[cat];
      const runningCount = catJobs.filter((j) => j.status === PaymentJobStatus.RUNNING).length;
      const failedCount = catJobs.filter((j) => j.status === PaymentJobStatus.FAILED).length;

      return {
        key: cat as TabKey,
        label: (
          <span className="inline-flex items-center gap-2 px-1">
            <span className="text-base">{meta.icon}</span>
            <span>{meta.label}</span>
            {catJobs.length > 0 && (
              <Badge
                count={runningCount > 0 ? runningCount : failedCount > 0 ? failedCount : catJobs.length}
                color={runningCount > 0 ? "#2563eb" : failedCount > 0 ? "#dc2626" : "#94a3b8"}
                size="small"
                overflowCount={99}
              />
            )}
          </span>
        ),
        children: (
          <div className="p-5">
            <CategoryPanel
              category={cat}
              jobs={catJobs}
              onLapse={handleLapse}
              onResumeDistribution={handleResumeDistribution}
              onResumeCreditor={handleResumeCreditor}
              onReschedule={handleOpenReschedule}
              lapsingId={lapsingId}
            />
          </div>
        ),
      };
    });

    const failedAttempts = attempts.filter((a) => a.status === PaymentAttemptStatus.FAILED).length;
    const succeededAttempts = attempts.filter((a) => a.status === PaymentAttemptStatus.SUCCEEDED).length;

    const attemptsTab = {
      key: CHARGE_ATTEMPTS_TAB,
      label: (
        <span className="inline-flex items-center gap-2 px-1">
          <span className="text-base">
            <MdBolt />
          </span>
          <span>Charge Attempts</span>
          {attempts.length > 0 && (
            <Badge
              count={failedAttempts > 0 ? failedAttempts : succeededAttempts > 0 ? succeededAttempts : attempts.length}
              color={failedAttempts > 0 ? "#dc2626" : succeededAttempts > 0 ? "#16a34a" : "#94a3b8"}
              size="small"
              overflowCount={99}
            />
          )}
        </span>
      ),
      children: (
        <div className="p-5">
          <ChargeAttemptsPanel attempts={attempts} isLoading={attemptsLoading} />
        </div>
      ),
    };

    return [...jobTabs, attemptsTab];
  }, [
    jobs,
    attempts,
    lapsingId,
    handleLapse,
    handleResumeDistribution,
    handleResumeCreditor,
    handleOpenReschedule,
    attemptsLoading,
  ]);

  if (!hasParams) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[400px] text-slate-400">
          <MdPayment className="text-5xl mb-4 text-slate-300" />
          <p className="text-lg font-medium text-slate-500">No data to display</p>
          <p className="text-sm mt-1">
            Provide both <code className="bg-slate-100 px-1 rounded">ledger_id</code> and{" "}
            <code className="bg-slate-100 px-1 rounded">application_id</code> URL parameters.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageTitle
        pageTitle={[
          { title: appkey ?? "", link: `/dashboard/application/${appkey}?tab=ledger` },
          { title: "Payment Job Monitor" },
        ]}
        title={`Payment Job Monitor - ${appkey}`}
      />

      <div className="min-h-screen bg-slate-50 p-6">
        <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-slate-800 tracking-tight">Payment Job Monitor</h1>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-xs text-slate-400 font-mono">
                Ledger ID: <span className="text-slate-600">{ledger_id}</span>
              </span>
              <span className="text-slate-300">·</span>
              <span className="text-xs text-slate-400 font-mono">
                Appkey: <span className="text-slate-600">{appkey ?? "—"}</span>
              </span>
            </div>
            <Button
              icon={<TbLink />}
              type="link"
              onClick={() =>
                window.open(
                  `/dashboard/transaction-logs?application_id=${application_id}&wallet_type=${WalletTypes.APPLICATION}`,
                  "_blank",
                )
              }
            >
              View Application Transaction Logs
            </Button>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div
              className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg border text-xs transition-colors"
              style={{
                background: autoRefreshEnabled ? "#eff6ff" : "#f9fafb",
                borderColor: autoRefreshEnabled ? "#bfdbfe" : "#e5e7eb",
              }}
            >
              <Switch size="small" checked={autoRefreshEnabled} onChange={handleToggleAutoRefresh} />
              <span className="text-slate-500 select-none">Auto-refresh</span>
              <Select
                size="small"
                value={refreshInterval}
                onChange={handleIntervalChange}
                options={REFRESH_OPTIONS.map((o) => ({ label: o.label, value: o.value }))}
                style={{ width: 62 }}
                disabled={!autoRefreshEnabled}
              />
              {autoRefreshEnabled && <CountdownDisplay seconds={countdown} />}
            </div>

            <Button
              size="small"
              icon={<RiRefreshLine className={isFetching ? "animate-spin" : ""} />}
              onClick={() => {
                refetch();
                refetchAttempts();
                if (autoRefreshEnabled) setCountdown(refreshInterval);
              }}
              loading={isLoading}
            >
              Refresh
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <Spin size="large" />
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <Tabs
              activeKey={activeTab}
              onChange={(k) => setActiveTab(k as TabKey)}
              items={tabItems}
              className="px-4 pt-3"
            />
          </div>
        )}
      </div>

      <Modal
        title="Reschedule payment job"
        open={!!reschedulingJob}
        onCancel={handleCloseReschedule}
        onOk={handleConfirmReschedule}
        okText="Reschedule"
        confirmLoading={rescheduleJob.isPending}
        destroyOnClose
      >
        {reschedulingJob && (
          <div className="space-y-4 pt-2">
            {/* <p className="text-sm text-slate-500">
              Currently scheduled for{" "}
              <span className="font-medium text-slate-700">
                {dayjs(reschedulingJob.scheduled_at).format("MMM D, YYYY h:mm A")}
              </span>
              . You can only move it earlier, not later.
            </p> */}

            <p className="text-sm text-slate-500">
              Currently scheduled for{" "}
              <span className="font-medium text-slate-700">
                {dayjs(reschedulingJob.scheduled_at).format("MMM D, YYYY h:mm A")}
              </span>
              . This will run it in <span className="font-medium text-slate-700">5 minutes from now</span> instead.
            </p>

            {/* <Radio.Group
              value={rescheduleMode}
              onChange={(e) => setRescheduleMode(e.target.value)}
              className="flex flex-col gap-2"
            >
              <Radio value="now">Run in 5 minutes from now</Radio>
              <Radio value="custom">Pick a custom time</Radio>
            </Radio.Group>

            {rescheduleMode === "custom" &&
              (() => {
                const now = dayjs();
                const ceiling = rescheduleCeiling ?? now;

                return (
                  <DatePicker
                    showTime={{ format: "HH:mm" }}
                    format="YYYY-MM-DD HH:mm"
                    value={customTime}
                    onChange={setCustomTime}
                    disabledDate={(d) => d.isBefore(now, "day") || d.isAfter(ceiling, "day")}
                    disabledTime={(d) => {
                      if (!d) return {};
                      const isToday = d.isSame(now, "day");
                      const isCeilingDay = d.isSame(ceiling, "day");

                      return {
                        disabledHours: () =>
                          Array.from({ length: 24 }, (_, h) => h).filter(
                            (h) => (isToday && h < now.hour()) || (isCeilingDay && h > ceiling.hour()),
                          ),
                        disabledMinutes: (selectedHour) =>
                          Array.from({ length: 60 }, (_, m) => m).filter(
                            (m) =>
                              (isToday && selectedHour === now.hour() && m < now.minute()) ||
                              (isCeilingDay && selectedHour === ceiling.hour() && m > ceiling.minute()),
                          ),
                      };
                    }}
                    className="w-full"
                    placeholder="Select date & time"
                  />
                );
              })()} */}
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
}
